import React, { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { Activity, Database, Users, ListChecks, Clock, RefreshCw, AlertTriangle, Cpu, HardDrive, Gauge, BellRing } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

const fmtSec = (s) => (s == null ? "—" : `${Number(s).toFixed(1)}s`);

// Alert thresholds
const PING_WARN = 100;   // ms
const PING_CRIT = 300;   // ms
const RATE_WARN = 200;   // submissions / minute
const RATE_CRIT = 500;   // submissions / minute

const levelOf = (val, warn, crit) => (val >= crit ? "crit" : val >= warn ? "warn" : "ok");
const LEVEL_STYLES = {
  ok:   { badge: "bg-[#2D6A4F]/10 text-[#2D6A4F]", dot: "bg-[#2D6A4F]", label: "Saudável" },
  warn: { badge: "bg-[#FF9F1C]/15 text-[#B26A00]", dot: "bg-[#FF9F1C]", label: "Atenção" },
  crit: { badge: "bg-[#E63946]/12 text-[#E63946]", dot: "bg-[#E63946]", label: "Sobrecarga" },
};

const StatCard = ({ icon: Icon, label, value, sub, color, testid, level }) => {
  const lv = level ? LEVEL_STYLES[level] : null;
  return (
    <div className={`rounded-2xl bg-white p-5 border ${level === "crit" ? "border-[#E63946]/40" : level === "warn" ? "border-[#FF9F1C]/40" : "border-[#0B1D3A]/5"}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <Icon className={color} size={22} />
        {lv && (
          <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full ${lv.badge}`} data-testid={`${testid}-badge`}>
            <span className={`h-1.5 w-1.5 rounded-full ${lv.dot}`} /> {lv.label}
          </span>
        )}
      </div>
      <p className="text-3xl font-outfit font-bold mt-3 text-[#0B1D3A] tabular-nums">{value}</p>
      <p className="text-sm text-[#0B1D3A]/60">{label}</p>
      {sub && <p className="text-xs text-[#0B1D3A]/40 mt-1">{sub}</p>}
    </div>
  );
};

const ThresholdEditor = ({ perf, onSaved }) => {
  const [cpu, setCpu] = useState(perf.alerts?.cpu_threshold_pct ?? 90);
  const [mem, setMem] = useState(perf.alerts?.mem_threshold_pct ?? 92);
  const [p95, setP95] = useState(perf.alerts?.p95_threshold_ms ?? 1200);
  const [hold, setHold] = useState(perf.alerts?.hold_sec ?? 120);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      await api.put("/admin/perf/thresholds", {
        alert_cpu_pct: Number(cpu),
        alert_mem_pct: Number(mem),
        alert_p95_ms: Number(p95),
        alert_hold_sec: Number(hold),
      });
      setMsg("Guardado.");
      if (onSaved) await onSaved();
    } catch (_) {
      setMsg("Erro ao guardar.");
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(""), 3000);
    }
  };
  const Field = ({ label, value, onChange, suffix, testid }) => (
    <label className="block text-sm font-semibold text-[#0B1D3A]">{label}
      <div className="mt-1 flex items-center gap-2">
        <input type="number" min="1" value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15 font-normal text-sm tabular-nums"
          data-testid={testid} />
        <span className="text-xs text-[#0B1D3A]/50">{suffix}</span>
      </div>
    </label>
  );
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <Field label="CPU crítico" value={cpu} onChange={setCpu} suffix="%" testid="th-cpu" />
        <Field label="Memória crítica" value={mem} onChange={setMem} suffix="%" testid="th-mem" />
        <Field label="Latência p95 crítica" value={p95} onChange={setP95} suffix="ms" testid="th-p95" />
        <Field label="Duração mínima" value={hold} onChange={setHold} suffix="s" testid="th-hold" />
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={save} disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0B1D3A] text-white font-bold text-sm disabled:opacity-60"
          data-testid="th-save">
          {saving ? "A guardar..." : "Guardar limites"}
        </button>
        {msg && <span className="text-xs text-[#0B1D3A]/60">{msg}</span>}
        <span className="text-xs text-[#0B1D3A]/50">
          Os níveis <em>Atenção</em> no painel aparecem antes destes valores (85% / 60% / 87% do crítico).
        </span>
      </div>
    </div>
  );
};

export default function AdminMetrics() {
  const [data, setData] = useState(null);
  const [perf, setPerf] = useState(null);
  const [err, setErr] = useState("");
  const [auto, setAuto] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [email1, setEmail1] = useState("");
  const [email2, setEmail2] = useState("");
  const [emailsLoaded, setEmailsLoaded] = useState(false);
  const [savingEmails, setSavingEmails] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        api.get("/admin/metrics"),
        api.get("/admin/perf").catch(() => ({ data: null })),
      ]);
      setData(r1.data);
      setPerf(r2.data);
      setErr("");
      setUpdatedAt(new Date());
    } catch (e) {
      setErr("Não foi possível carregar as métricas.");
    }
  }, []);

  // Load configured alert emails once.
  useEffect(() => {
    if (emailsLoaded) return;
    api.get("/admin/settings/base").then((r) => {
      setEmail1(r.data?.alert_email_1 || "");
      setEmail2(r.data?.alert_email_2 || "");
      setEmailsLoaded(true);
    }).catch(() => setEmailsLoaded(true));
  }, [emailsLoaded]);

  const saveEmails = async () => {
    setSavingEmails(true);
    try {
      await api.put("/admin/settings", {
        alert_email_1: email1.trim(),
        alert_email_2: email2.trim(),
      });
      await load();
    } catch (_) {
      setErr("Erro ao guardar os emails de alerta.");
    } finally {
      setSavingEmails(false);
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!auto) return;
    const id = setInterval(load, 10000); // refresh every 10s
    return () => clearInterval(id);
  }, [auto, load]);

  const pingLevel = !data?.db?.ok ? "crit" : levelOf(data.db.ping_ms, PING_WARN, PING_CRIT);
  const rate1m = data?.activity?.last_1m || 0;
  const rateLevel = levelOf(rate1m, RATE_WARN, RATE_CRIT);
  const order = { ok: 0, warn: 1, crit: 2 };
  const overall = order[pingLevel] >= order[rateLevel] ? pingLevel : rateLevel;
  const pingColor = pingLevel === "ok" ? "text-[#2D6A4F]" : pingLevel === "warn" ? "text-[#FF9F1C]" : "text-[#E63946]";

  return (
    <div data-testid="admin-metrics">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <h1 className="font-outfit text-3xl font-bold text-[#0B1D3A]">Health &amp; Métricas</h1>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-[#0B1D3A]/70 cursor-pointer">
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)}
              className="h-4 w-4 accent-[#005F73]" data-testid="metrics-autorefresh" />
            Atualização automática (10s)
          </label>
          <button onClick={load} data-testid="metrics-refresh-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0B1D3A] text-white text-sm font-medium hover:bg-[#0B1D3A]/90 transition">
            <RefreshCw size={16} /> Atualizar
          </button>
        </div>
      </div>
      <p className="text-[#0B1D3A]/60 mb-6">
        Monitorização ao vivo da aplicação{updatedAt ? ` — atualizado às ${updatedAt.toLocaleTimeString()}` : ""}
      </p>

      {err && (
        <div className="rounded-xl bg-[#E63946]/10 border border-[#E63946]/30 text-[#E63946] px-4 py-3 mb-6 flex items-center gap-2" data-testid="metrics-error">
          <AlertTriangle size={18} /> {err}
        </div>
      )}

      {!data ? (
        <p className="text-[#0B1D3A]/50">A carregar…</p>
      ) : (
        <>
          <div className={`rounded-2xl px-5 py-4 mb-6 flex items-center gap-3 ${overall === "ok" ? "bg-[#2D6A4F]/[0.07] border border-[#2D6A4F]/25" : overall === "warn" ? "bg-[#FF9F1C]/10 border border-[#FF9F1C]/35" : "bg-[#E63946]/[0.08] border border-[#E63946]/35"}`}
               data-testid="metrics-status-banner" data-status={overall}>
            <span className={`relative flex h-3 w-3`}>
              {overall !== "ok" && <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-60 ${LEVEL_STYLES[overall].dot}`} />}
              <span className={`relative inline-flex rounded-full h-3 w-3 ${LEVEL_STYLES[overall].dot}`} />
            </span>
            <div>
              <p className={`font-outfit font-bold ${overall === "ok" ? "text-[#2D6A4F]" : overall === "warn" ? "text-[#B26A00]" : "text-[#E63946]"}`}>
                {overall === "ok" ? "Sistema saudável" : overall === "warn" ? "Atenção — carga elevada" : "Sobrecarga detetada"}
              </p>
              <p className="text-xs text-[#0B1D3A]/60">
                BD: {data.db.ok ? `${data.db.ping_ms} ms` : "OFFLINE"} · {rate1m} submissões no último minuto
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <StatCard testid="metric-db" icon={Database} color={pingColor} level={pingLevel}
              label="Base de dados" value={data.db.ok ? `${data.db.ping_ms} ms` : "OFFLINE"}
              sub={data.db.ok ? "Latência do ping" : "Sem ligação"} />
            <StatCard testid="metric-rate-1m" icon={Activity} color="text-[#FF6B6B]" level={rateLevel}
              label="Provas (último minuto)" value={rate1m} sub="Ritmo de submissões" />
            <StatCard testid="metric-active-5m" icon={Clock} color="text-[#005F73]"
              label="Provas (últimos 5 min)" value={data.activity.last_5m} />
            <StatCard testid="metric-active-24h" icon={Clock} color="text-[#2D6A4F]"
              label="Provas (24 h)" value={data.activity.last_24h} />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard testid="metric-total-parts" icon={Users} color="text-[#FF6B6B]"
              label="Participações totais" value={data.totals.participations} />
            <StatCard testid="metric-total-creds" icon={Users} color="text-[#005F73]"
              label="Credenciais" value={data.totals.credentials} />
            <StatCard testid="metric-total-enroll" icon={ListChecks} color="text-[#2D6A4F]"
              label="Inscrições" value={data.totals.enrollments} />
            <StatCard testid="metric-total-quizzes" icon={ListChecks} color="text-[#FFB703]"
              label="Quizzes" value={data.totals.quizzes} />
          </div>

          {perf && (() => {
            const cpu = perf.system?.cpu_percent ?? 0;
            const mem = perf.system?.mem_percent ?? 0;
            const p95 = perf.requests?.last_1m?.p95_ms ?? 0;
            const rps = perf.requests?.last_1m?.rps ?? 0;
            const err5m = (perf.requests?.last_5m?.server_err ?? 0);
            const rl5m = (perf.requests?.last_5m?.rate_limited ?? 0);
            // Thresholds come from backend (DB settings → defaults). SCALE tier
            // tolerates higher CPU and p95 before signalling warn/crit.
            const cpuCrit = perf.alerts?.cpu_threshold_pct ?? 90;
            const cpuWarn = Math.max(60, Math.round(cpuCrit * 0.85));
            const p95Crit = perf.alerts?.p95_threshold_ms ?? 1200;
            const p95Warn = Math.max(400, Math.round(p95Crit * 0.6));
            const memCrit = perf.alerts?.mem_threshold_pct ?? 92;
            const memWarn = Math.max(70, Math.round(memCrit * 0.87));
            const cpuLv = levelOf(cpu, cpuWarn, cpuCrit);
            const memLv = levelOf(mem, memWarn, memCrit);
            const p95Lv = levelOf(p95, p95Warn, p95Crit);
            const alerts = perf.alerts?.recent || [];
            const rec = (cpuLv === "crit" || p95Lv === "crit")
              ? "⚠️ Sobrecarga sustentada — recomendo subir de tier ou aumentar réplicas (HPA) antes do pico."
              : (cpuLv === "warn" || p95Lv === "warn")
              ? "ℹ️ Carga a aproximar-se do limite do tier atual. Monitoriza nos próximos minutos."
              : "✅ Dentro de margens saudáveis.";
            return (
              <>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <h2 className="font-outfit font-bold text-[#0B1D3A]">Performance ao vivo</h2>
                  <span className="text-xs text-[#0B1D3A]/50">Processo pid {perf.pid} · janela 5 min</span>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <StatCard testid="perf-cpu" icon={Cpu} color="text-[#005F73]" level={cpuLv}
                    label="CPU" value={`${cpu}%`} sub="Carga do worker" />
                  <StatCard testid="perf-mem" icon={HardDrive} color="text-[#7C3AED]" level={memLv}
                    label="Memória" value={`${mem}%`} sub={`${perf.system?.process_rss_mb ?? 0} MB RSS`} />
                  <StatCard testid="perf-rps" icon={Activity} color="text-[#FF6B6B]"
                    label="Pedidos/seg (1 min)" value={rps.toFixed(2)} sub={`${perf.requests?.last_1m?.count ?? 0} req / 1 min`} />
                  <StatCard testid="perf-p95" icon={Gauge} color="text-[#FFB703]" level={p95Lv}
                    label="Latência p95 (1 min)" value={`${p95} ms`} sub={`p99 ${perf.requests?.last_1m?.p99_ms ?? 0} ms`} />
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <StatCard testid="perf-ok" icon={Activity} color="text-[#2D6A4F]"
                    label="2xx (5 min)" value={perf.requests?.last_5m?.ok ?? 0} />
                  <StatCard testid="perf-4xx" icon={AlertTriangle} color="text-[#FF9F1C]"
                    label="4xx (5 min)" value={perf.requests?.last_5m?.client_err ?? 0} />
                  <StatCard testid="perf-5xx" icon={AlertTriangle} color="text-[#E63946]" level={err5m > 0 ? "warn" : "ok"}
                    label="5xx (5 min)" value={err5m} />
                  <StatCard testid="perf-429" icon={BellRing} color="text-[#0B1D3A]"
                    label="Bloqueios (429, 5 min)" value={rl5m} sub="Pedidos travados pelo rate-limit" />
                </div>
                <div className={`rounded-xl px-4 py-3 mb-6 text-sm font-medium ${
                  cpuLv === "crit" || p95Lv === "crit" ? "bg-[#E63946]/10 text-[#E63946] border border-[#E63946]/30"
                  : cpuLv === "warn" || p95Lv === "warn" ? "bg-[#FF9F1C]/12 text-[#B26A00] border border-[#FF9F1C]/30"
                  : "bg-[#2D6A4F]/8 text-[#2D6A4F] border border-[#2D6A4F]/25"}`}
                     data-testid="perf-recommendation">
                  {rec}
                </div>
                <div className="rounded-2xl bg-white border border-[#0B1D3A]/5 p-5 mb-6" data-testid="perf-alert-emails">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <h2 className="font-outfit font-bold text-[#0B1D3A]">Destinatários dos alertas por email</h2>
                    <span className="text-xs text-[#0B1D3A]/50">
                      Em uso: {(perf.alerts?.recipients || []).join(", ") || "nenhum"}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <label className="block text-sm font-semibold text-[#0B1D3A]">Email 1
                      <input type="email" value={email1} onChange={(e) => setEmail1(e.target.value)}
                        placeholder="alertas@empresa.pt" autoComplete="off"
                        className="mt-1 w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15 font-normal text-sm"
                        data-testid="perf-alert-email-1" />
                    </label>
                    <label className="block text-sm font-semibold text-[#0B1D3A]">Email 2
                      <input type="email" value={email2} onChange={(e) => setEmail2(e.target.value)}
                        placeholder="opcional@empresa.pt" autoComplete="off"
                        className="mt-1 w-full px-3 py-2 rounded-lg border border-[#0B1D3A]/15 font-normal text-sm"
                        data-testid="perf-alert-email-2" />
                    </label>
                  </div>
                  <button onClick={saveEmails} disabled={savingEmails}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0B1D3A] text-white font-bold text-sm disabled:opacity-60"
                    data-testid="perf-alert-emails-save">
                    {savingEmails ? "A guardar..." : "Guardar emails"}
                  </button>
                  <p className="text-xs text-[#0B1D3A]/50 mt-2">Se os dois ficarem vazios, os alertas vão para o email padrão do admin.</p>
                </div>

                <div className="rounded-2xl bg-white border border-[#0B1D3A]/5 p-5 mb-6" data-testid="perf-thresholds">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <h2 className="font-outfit font-bold text-[#0B1D3A]">Limites de alerta (tier atual)</h2>
                    <span className="text-xs text-[#0B1D3A]/50">
                      Alerta apenas se sustentado ≥ {perf.alerts?.hold_sec ?? 120}s
                    </span>
                  </div>
                  <ThresholdEditor perf={perf} onSaved={load} />
                </div>

                <div className="flex items-center justify-between gap-2 mb-3">
                  <h2 className="font-outfit font-bold text-[#0B1D3A]">Alertas recentes</h2>
                  <span className="text-xs text-[#0B1D3A]/50">
                    Limites: CPU &gt; {perf.alerts?.cpu_threshold_pct}% · p95 &gt; {perf.alerts?.p95_threshold_ms} ms (sustentado &ge; 2 min)
                  </span>
                </div>
                <div className="rounded-2xl bg-white border border-[#0B1D3A]/5 overflow-hidden mb-8" data-testid="perf-alerts-list">
                  {alerts.length === 0 ? (
                    <p className="px-4 py-6 text-[#0B1D3A]/40 text-sm">Sem alertas registados.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-[#0B1D3A]/[0.03] text-[#0B1D3A]/60 text-left">
                        <tr>
                          <th className="px-4 py-2 font-semibold">Data</th>
                          <th className="px-4 py-2 font-semibold">Regra</th>
                          <th className="px-4 py-2 font-semibold">Título</th>
                          <th className="px-4 py-2 font-semibold">Detalhe</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alerts.map((a) => (
                          <tr key={a.id} className="border-t border-[#0B1D3A]/5" data-testid={`perf-alert-${a.id}`}>
                            <td className="px-4 py-2 whitespace-nowrap text-[#0B1D3A]/70 text-xs tabular-nums">{(a.created_at || "").replace("T", " ").slice(0, 19)}</td>
                            <td className="px-4 py-2 text-xs"><span className="px-2 py-0.5 rounded-full bg-[#0B1D3A]/5 text-[#0B1D3A]/70 uppercase">{a.rule}</span></td>
                            <td className="px-4 py-2 font-semibold text-[#0B1D3A]">{a.title}</td>
                            <td className="px-4 py-2 text-[#0B1D3A]/70">{a.detail}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            );
          })()}

          <h2 className="font-outfit font-bold text-[#0B1D3A] mb-3">Submissões nas últimas 24 h (por hora)</h2>
          <div className="rounded-2xl bg-white border border-[#0B1D3A]/5 p-4 mb-8" data-testid="metrics-timeline-chart">
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={data.timeline || []} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="subFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF6B6B" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#FF6B6B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#0B1D3A12" vertical={false} />
                <XAxis dataKey="hour" tick={{ fontSize: 11, fill: "#0B1D3A99" }} interval={2} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#0B1D3A99" }} tickLine={false} axisLine={false} width={36} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #0B1D3A15", fontSize: 13 }}
                  labelFormatter={(l) => `${l}`}
                  formatter={(v) => [v, "Submissões"]}
                />
                <Area type="monotone" dataKey="count" stroke="#FF6B6B" strokeWidth={2.5} fill="url(#subFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <h2 className="font-outfit font-bold text-[#0B1D3A] mb-3">Por prova</h2>
          <div className="rounded-2xl bg-white border border-[#0B1D3A]/5 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="metrics-per-quiz-table">
                <thead>
                  <tr className="bg-[#0B1D3A]/[0.03] text-[#0B1D3A]/60 text-left">
                    <th className="px-4 py-3 font-semibold">Prova</th>
                    <th className="px-4 py-3 font-semibold">Tipo</th>
                    <th className="px-4 py-3 font-semibold text-right">Credenciais</th>
                    <th className="px-4 py-3 font-semibold text-right">Participações</th>
                    <th className="px-4 py-3 font-semibold text-right">Concluídas</th>
                    <th className="px-4 py-3 font-semibold text-right">Tempo esgotado</th>
                    <th className="px-4 py-3 font-semibold text-right">Tempo médio</th>
                  </tr>
                </thead>
                <tbody>
                  {data.per_quiz.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-6 text-center text-[#0B1D3A]/40">Sem provas.</td></tr>
                  )}
                  {data.per_quiz.map((q) => (
                    <tr key={q.quiz_id} className="border-t border-[#0B1D3A]/5" data-testid={`metrics-row-${q.quiz_id}`}>
                      <td className="px-4 py-3 font-medium text-[#0B1D3A]">
                        {q.name_pt || q.quiz_id}
                        {q.active ? <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#2D6A4F]/10 text-[#2D6A4F]">ativa</span> : null}
                      </td>
                      <td className="px-4 py-3 text-[#0B1D3A]/60">{q.type}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{q.credentials}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold">{q.participations}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-[#2D6A4F]">{q.completed}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-[#E63946]">{q.timed_out}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{fmtSec(q.avg_seconds)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
